/*
 * Remembers whether this browser has ever held a signed-in session, so a
 * 401/419 can be told apart from "this visitor was simply never logged in".
 *
 * Both apps bootstrap by probing GET /api/auth/me on first paint, and for a
 * guest that probe 401s - indistinguishable, to the API client, from a session
 * that just expired. Without this crumb every first-time visitor (a fresh
 * incognito window, say) was greeted with "your session expired".
 *
 * A module-level flag plus a localStorage crumb: the crumb survives a closed
 * tab, which is the case worth keeping - you were logged in last week, the
 * cookie has since expired, and coming back deserves the notice. The flag
 * covers the same tab when storage is unavailable (private modes, blocked site
 * data) and keeps a session ended in another tab noticeable in this one.
 */

const STORAGE_KEY = 'ottd.had-session';

let sessionSeen = false;

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Accessing localStorage itself throws when site data is blocked.
    return null;
  }
}

/** Records that a session is (or was) live - called once it is established. */
export function rememberSession(): void {
  sessionSeen = true;
  try {
    storage()?.setItem(STORAGE_KEY, '1');
  } catch {
    // Quota or a private window: the in-memory flag still covers this tab.
  }
}

/** Forgets the session - on an explicit logout, and once an expiry is reported. */
export function forgetSession(): void {
  sessionSeen = false;
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unreadable.
  }
}

/** True when this browser held a session that is now gone. */
export function hadSession(): boolean {
  if (sessionSeen) return true;
  try {
    return storage()?.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
